import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  EdlinkService, EdlinkCourse, EdlinkPage, EdlinkFile,
} from 'src/app/_services/edlink.service';

/**
 * "Import from your school account" (Edlink) — create a Tangram from a course's
 * pages & files. Connection is the Edlink SSO session (no manual token form).
 * Step 1: pick a course. Step 2: pick pages/files. Then import → one pathway.
 */
@Component({
  selector: 'tangram-create-from-edlink',
  templateUrl: './create-from-edlink.component.html',
  styleUrls: ['./create-from-edlink.component.scss'],
})
export class CreateFromEdlinkComponent implements OnInit {
  step = 1;
  checking = true;
  connected = false;

  loadingCourses = false;
  loadingContent = false;
  importing = false;

  courses: EdlinkCourse[] = [];
  selectedCourse: EdlinkCourse | null = null;
  pages: EdlinkPage[] = [];
  files: EdlinkFile[] = [];

  constructor(
    private edlinkService: EdlinkService,
    private toaster: ToastrService,
    private router: Router,
  ) {}

  ngOnInit() { this.checkStatus(); }

  connect() { this.edlinkService.startSso(); }

  get selectedPages(): EdlinkPage[] { return (this.pages || []).filter((p) => p.selected); }
  get selectedFiles(): EdlinkFile[] { return (this.files || []).filter((f) => f.selected); }

  get isStepValid(): boolean {
    if (this.step === 1) return !!this.selectedCourse;
    if (this.step === 2) return this.selectedPages.length > 0 || this.selectedFiles.length > 0;
    return true;
  }

  checkStatus() {
    this.checking = true;
    this.edlinkService.getStatus().subscribe(
      (res) => {
        this.checking = false;
        if (res && res.data && res.data.connected) {
          this.connected = true;
          this.loadCourses();
        } else {
          this.connected = false;
        }
      },
      () => { this.checking = false; this.connected = false; },
    );
  }

  loadCourses() {
    // TODO: re-enable course list fetch once LTI context flow is verified.
    // this.loadingCourses = true;
    // this.edlinkService.listCourses().subscribe(
    //   (res) => {
    //     this.loadingCourses = false;
    //     this.courses = ((res && res.data) || []).filter((c) => c.role === 'teacher');
    //     if (this.courses.length === 0) {
    //       this.toaster.info('No courses where you are a teacher were found.');
    //     }
    //     this.applyLtiContext();
    //   },
    //   (err) => {
    //     this.loadingCourses = false;
    //     this.toaster.error((err && err.error && err.error.message) || 'Failed to load courses');
    //     if (err && err.status === 412) this.connected = false;
    //   },
    // );
    this.applyLtiContext();
  }

  private applyLtiContext() {
    const raw = localStorage.getItem('edlinkLtiContext');
    if (!raw) return;
    try {
      const ctx = JSON.parse(raw);
      if (ctx.intent !== 'class' || !ctx.class || !ctx.class.id) return;
      // Use the matched course from the list, or build from LTI context directly
      // (LTI-only integrations may return a sparse course list).
      const course = this.courses.find((c) => c.id === ctx.class.id)
        || { id: ctx.class.id, name: ctx.class.name || 'Canvas Course', state: ctx.class.state, role: 'teacher' as const };
      this.selectCourse(course);
      this.step = 2;
    } catch (_) {}
  }

  selectCourse(course: EdlinkCourse) {
    this.selectedCourse = course;
    this.loadContent(course.id);
  }

  loadContent(courseId: string) {
    this.loadingContent = true;
    this.pages = [];
    this.files = [];
    let done = 0;
    const finish = () => { if (++done === 2) this.loadingContent = false; };

    this.edlinkService.listPages(courseId).subscribe(
      (res) => { this.pages = ((res && res.data) || []).map((p) => ({ ...p, selected: false })); finish(); },
      () => finish(),
    );
    this.edlinkService.listFiles(courseId).subscribe(
      (res) => { this.files = ((res && res.data) || []).map((f) => ({ ...f, selected: false })); finish(); },
      () => finish(),
    );
  }

  togglePage(p: EdlinkPage) { p.selected = !p.selected; }
  toggleFile(f: EdlinkFile) { f.selected = !f.selected; }
  selectAll() { this.pages.forEach((p) => (p.selected = true)); this.files.forEach((f) => (f.selected = true)); }
  deselectAll() { this.pages.forEach((p) => (p.selected = false)); this.files.forEach((f) => (f.selected = false)); }

  next() { if (this.isStepValid && this.step < 2) this.step++; }
  prev() { if (this.step > 1) this.step--; }

  importCourse() {
    if (!this.selectedCourse) return;
    if (this.selectedPages.length === 0 && this.selectedFiles.length === 0) {
      this.toaster.error('Select at least one page or file to import');
      return;
    }
    this.importing = true;
    this.edlinkService.importCourse({
      courseId: this.selectedCourse.id,
      courseName: this.selectedCourse.name,
      pages: this.selectedPages.map((p) => ({ id: p.id, title: p.title })),
      files: this.selectedFiles.map((f) => ({ id: f.id, displayName: f.displayName, url: f.url, mediaType: f.mediaType })),
    }).subscribe(
      (res: any) => {
        this.importing = false;
        if (res && res.success) {
          localStorage.removeItem('edlinkLtiContext');
          this.toaster.success(`Imported ${res.data.mediaCount} item(s) from the course`);
          this.router.navigate(['/share-tangram'], { queryParams: { pathwayId: res.data.pathwayId } });
        } else {
          this.toaster.error((res && res.message) || 'Import failed');
        }
      },
      (err) => {
        this.importing = false;
        this.toaster.error((err && err.error && err.error.message) || 'Failed to import course');
      },
    );
  }
}
